import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Megaphone } from "lucide-react";
import { PostcardPreview } from "@/components/PostcardPreview";

const GROUPS = [
  ["Postcard", [["postcard_headline", "Front headline"], ["postcard_subhead", "Front subheadline"], ["postcard_message", "Back message", true], ["qr_caption", "QR code caption"]]],
  ["Email", [["email_subject", "Subject (leave subject and body empty for AI-written emails)"], ["email_body", "Body", true]]],
  ["Texting", [["sms_text", "Daily Autopilot text", true], ["qr_sms_text", "Text sent right after a QR scan", true]]],
  ["Ava's calls", [["call_script", "Your talking points for Ava (optional)", true], ["voicemail_text", "Voicemail Ava leaves", true]]],
];

const Field = ({ k, label, long, value, onChange }) => (
  <div className="sm:col-span-2">
    <Label className="text-xs">{label}</Label>
    {long
      ? <Textarea rows={3} value={value || ""} onChange={(e) => onChange(k, e.target.value)} data-testid={`outreach-${k}`} />
      : <Input value={value || ""} onChange={(e) => onChange(k, e.target.value)} data-testid={`outreach-${k}`} />}
  </div>
);

export const SuretyOutreachCard = () => {
  const [d, setD] = useState(null);
  const [tags, setTags] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/surety/outreach").then((r) => { setD(r.data.settings); setTags(r.data.tags); }).catch((e) => toast.error(apiError(e))); }, []);
  if (!d) return null;
  const set = (k, v) => setD({ ...d, [k]: v });
  const save = async () => {
    setBusy(true);
    try { setD((await api.put("/surety/outreach", d)).data.settings); toast.success("Outreach saved"); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Card className="mt-6 p-6" data-testid="surety-outreach-card">
      <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Megaphone className="h-5 w-5" /> My outreach</h3>
      <p className="mb-4 text-sm text-slate-600">Edit your postcards, emails, texts and what Ava says. You can use: {tags.map((t) => `{${t}}`).join(" ")}. Your NPN and carrier always appear on postcards and in Ava's introduction.</p>
      {GROUPS.map(([title, fields]) => (
        <div key={title} className="mb-5 border-t border-slate-100 pt-4">
          <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-500">{title}</h4>
          {title === "Postcard" && (
            <div className="mb-3 flex items-center gap-3">
              <Switch checked={!!d.qr_enabled} onCheckedChange={(v) => set("qr_enabled", v)} data-testid="outreach-qr-enabled" />
              <span className="text-sm text-slate-700">Print a QR code. When a contractor scans it, Ava calls you with a heads-up, then calls and texts them, then calls and texts you with the result.</span>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {fields.map(([k, label, long]) => <Field key={k} k={k} label={label} long={long} value={d[k]} onChange={set} />)}
          </div>
          {title === "Postcard" && <PostcardPreview draft={d} />}
        </div>
      ))}
      <Button onClick={save} disabled={busy} data-testid="surety-outreach-save">Save outreach</Button>
    </Card>
  );
};
